import { useState, useContext } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { ReactComponent as PlusSquare } from "bootstrap-icons/icons/plus-square.svg";
import { ReactComponent as PencilSquare } from "bootstrap-icons/icons/pencil-square.svg";
import { ReactComponent as Trash } from "bootstrap-icons/icons/trash3.svg";
import { ReactComponent as Eye } from "bootstrap-icons/icons/eye.svg";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import Spinner from "../spinner/Spinner";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import CustomStyles from "../../components/utils/CustomStyles";
import { AuthContext } from "../../AuthProvider/AuthProvider";
import { useDenominations, useDeleteDenomination } from "../../hooks/useAppData";

const NETWORK_MAP = { 1: "MTN", 2: "Airtel", 3: "Glo", 4: "9Mobile" };

const DenominationList = () => {
    const [filterText, setFilterText] = useState("");
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const [confirmationLoading, setConfirmationLoading] = useState(false);
    const { permissions } = useContext(AuthContext);
    const navigate = useNavigate();

    const { data: denominationsResponse, isLoading: loading } = useDenominations();
    const denominations = denominationsResponse?.data || [];
    const deleteDenominationMutation = useDeleteDenomination();

    const handleFilter = (e) => {
        setFilterText(e.target.value);
        setResetPaginationToggle(!resetPaginationToggle);
    };

    const handleDeleteConfirmation = (id) => {
        setConfirmationLoading(true);
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        }).then((result) => {
            setConfirmationLoading(false);
            if (result.isConfirmed) {
                deleteDenominationMutation.mutate(id);
            }
        });
    };

    const ExpandableComponent = ({ data }) => (
        <div className="flex justify-start p-4 relative">
            {permissions.some((p) => p.name === "physical.product.edit") && (
                <Link
                    to="/admin/denomination/update"
                    state={{ denomination: data }}
                    className="mx-2 group"
                >
                    <PencilSquare className="w-5 h-5 text-dash4" />
                    <div className="absolute ml-4 top-full left-2/2 transform -translate-x-1/2 mb-1 hidden group-hover:block text-dash5 text-xs whitespace-nowrap">
                        Update Denomination
                    </div>
                </Link>
            )}
        </div>
    );

    const columns = [
        {
            name: "Amount",
            selector: (row) => row.amount,
            sortable: true,
            width: "180px",
        },
        {
            name: "Count",
            selector: (row) => row.count,
            sortable: true,
            width: "120px",
        },
        {
            name: "Network",
            selector: (row) => NETWORK_MAP[row.network_id] ?? "—",
            sortable: true,
            width: "150px",
        },
        {
            name: "Branch Product Unit",
            selector: (row) => row.branch_product_unit?.name ?? "—",
            sortable: true,
            width: "250px",
        },
    ];

    const filteredItems = denominations.filter((item) =>
        Object.values(item).some((val) =>
            String(val).toLowerCase().includes(filterText.toLowerCase())
        )
    );

    const sortedData = [...filteredItems].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );

    return (
        <div>
            <MainLayouts>
                <div className="relative overflow-x-auto sm:rounded-lg py-6 w-full">
                    <section
                        className="w-full pr-[3%] pt-3 my-3"
                        style={{ backgroundColor: "#F7F7F7" }}
                    >
                        <nav className="bg-gray-200 py-0 px-4 rounded-lg">
                            <ol className="list-none p-0 inline-flex mb-2">
                                <li className="flex items-center">
                                    <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                                        Denomination
                                    </p>
                                    <ChevronRightIcon />
                                </li>
                                <li className="flex items-center">
                                    <p className="text-orange1">List</p>
                                </li>
                            </ol>
                        </nav>
                    </section>

                    <div className="pb-4 bg-white dark:bg-gray-900 flex flex-wrap justify-between">
                        <div className="relative w-full sm:w-1/2 mb-4 sm:mb-0">
                            <input
                                type="search"
                                className="block w-full px-3 py-2 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                                placeholder="Search"
                                value={filterText}
                                onChange={handleFilter}
                            />
                        </div>
                        {permissions.some((p) => p.name === "physical.product.create") && (
                            <Link
                                to="/admin/denomination/create"
                                className="m-1 w-full sm:w-auto text-dGrey3 bg-darkGreen3 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                            >
                                <span className="flex justify-center sm:justify-start mx-2">
                                    <PlusSquare className="mr-2" />
                                    Add Denomination
                                </span>
                            </Link>
                        )}
                    </div>

                    {loading ? (
                        <Spinner loading={loading} />
                    ) : (
                        <DataTable
                            columns={columns}
                            data={sortedData}
                            pagination
                            striped
                            highlightOnHover
                            customStyles={CustomStyles}
                            noDataComponent={<div>No denominations found</div>}
                            expandableRows
                            expandableRowsComponent={ExpandableComponent}
                        />
                    )}
                </div>
            </MainLayouts>
        </div>
    );
};

export default DenominationList;
